import React, { Suspense, lazy, useState } from 'react';
import { useSchool, DEFAULT_SCHOOL_ID } from '../contexts/SchoolContext';
import { SubscriptionPlansModal } from './SubscriptionPlansModal';
import { GRACE_DAYS, fmtDate, useSubscription } from '../services/billingClient';

const BillingPage = lazy(() => import('./billing/BillingPage'));

interface Props {
  children: React.ReactNode;
  /** let these screens through (the home/login page must always be reachable) */
  bypass?: boolean;
  /** shown on every gate screen, so nobody is stuck on it */
  onSignOut?: () => void;
  /** the signed-in person's role: Super Admin is never paused, and only the school admin sees Billing when paused */
  role?: string;
}

export const SubscriptionGate: React.FC<Props> = ({ children, bypass, onSignOut, role }) => {
  const { currentSchool, loading } = useSchool();
  const [plansOpen, setPlansOpen] = useState(false);
  const sub = useSubscription(currentSchool?.id);

  // Loading — show nothing to avoid flash
  if (loading || bypass || role === 'super_admin') return <>{children}</>;

  // Billing (functions/src/billing.ts): after the paid-until date, 14 days of reminders, then paused.
  if (sub.state === 'paused' && sub.billing) {
    if (role === 'admin') return (
      <div className="min-h-screen bg-slate-50 p-4 dark:bg-slate-900 md:p-8">
        <div className="mx-auto mb-4 max-w-5xl rounded-2xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-500/10 dark:text-rose-200">
          <b>{currentSchool?.name}'s portal is paused.</b> The subscription ended on {fmtDate(sub.billing.paidUntil)}. Staff, students and parents see a notice until it is renewed. No data has been deleted, and the portal opens again as soon as payment is confirmed.
          {onSignOut && <button onClick={onSignOut} className="ml-2 font-bold underline">Sign out</button>}
        </div>
        <Suspense fallback={null}><BillingPage schoolId={currentSchool!.id} /></Suspense>
      </div>
    );
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg,#1a0b00,#0f172a)' }}>
        <div className="max-w-md text-center">
          <h2 className="text-2xl font-black text-white mb-3">The portal is paused</h2>
          <p className="text-slate-400">{currentSchool?.name}'s MEF EduSuite subscription needs to be renewed by the school. Your work and results are kept safe, and everything comes back as soon as the school renews. Please check with the school office.</p>
          {signOutButton(onSignOut)}
        </div>
      </div>
    );
  }
  const banner = sub.state === 'grace' && sub.billing && ['admin', 'teacher'].includes(role || '') && (
    <div className="sticky top-0 z-[90] bg-amber-500 px-4 py-2 text-center text-sm font-bold text-amber-950">
      The school's MEF EduSuite subscription ended on {fmtDate(sub.billing.paidUntil)}. The portal pauses on {fmtDate(sub.billing.paidUntil + GRACE_DAYS * 864e5)} unless it is renewed{role === 'admin' ? ' — open Billing in the menu to pay.' : '. Please let the school admin know.'}
    </div>
  );
  if (banner) return <>{banner}{children}</>;

  const signOut = signOutButton(onSignOut);

  // Main school always passes through
  if (!currentSchool || currentSchool.id === DEFAULT_SCHOOL_ID) return <>{children}</>;

  const status = currentSchool.whitelabelStatus;

  // Fully active OR admin-approved (approval = payment already verified by admin) — pass through.
  // No status at all = a school the super admin added directly (not through a partnership
  // application), so there is nothing to wait for.
  if (!status || status === 'active' || status === 'approved') return <>{children}</>;

  // Pending admin approval — not yet approved
  if (status === 'pending_approval') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg,#0b1530,#0f172a)' }}>
        <div className="max-w-md text-center">
          <div className="text-6xl mb-5">⏳</div>
          <h2 className="text-2xl font-black text-white mb-3">Application Under Review</h2>
          <p className="text-slate-400 mb-6">
            Your partnership application for <strong className="text-white">{currentSchool.name}</strong> has been received and is currently under review by the MEF EduSuite team. You will receive an email notification once your account is approved.
          </p>
          <div className="p-4 rounded-2xl text-left" style={{ background: 'rgba(27,56,112,0.15)', border: '1px solid rgba(27,56,112,0.35)' }}>
            <p className="text-blue-400 font-bold text-sm mb-2">What to expect:</p>
            <p className="text-slate-400 text-sm">• Review typically takes 1–2 business days</p>
            <p className="text-slate-400 text-sm">• Confirmation email sent to your registered address</p>
            <p className="text-slate-400 text-sm">• Once approved, return here to select your subscription</p>
          </div>
          <p className="text-slate-500 text-xs mt-5">
            Questions? <a href="mailto:info@muyikaratu.com" className="text-blue-400 hover:underline">info@muyikaratu.com</a>
          </p>
          {signOut}
        </div>
      </div>
    );
  }

  // Approved but no subscription — show plan picker
  if (status === 'approved') {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg,#0b1530,#0f172a)' }}>
          <div className="max-w-md text-center">
            <div className="text-6xl mb-5">🎉</div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-xs font-black" style={{ background: 'rgba(61,155,61,0.15)', border: '1px solid rgba(61,155,61,0.35)', color: '#4ABF4A' }}>
              ✓ ACCOUNT APPROVED
            </div>
            <h2 className="text-2xl font-black text-white mb-3">Welcome, {currentSchool.name}!</h2>
            <p className="text-slate-400 mb-8">
              Your account has been approved. Subscribe to a plan to unlock your school's full portal — student dashboards, CBT, LMS, IQA Hub, and more.
            </p>
            <button
              onClick={() => setPlansOpen(true)}
              className="px-8 py-4 rounded-2xl font-black text-white text-base transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg,#1B3870,#3D9B3D)', boxShadow: '0 8px 32px rgba(27,56,112,0.5)' }}
            >
              Choose a Subscription Plan →
            </button>
            <p className="text-slate-500 text-xs mt-5">
              Priced per student per term · Paystack, Flutterwave, card or bank transfer
            </p>
          </div>
        </div>
        <SubscriptionPlansModal open={plansOpen} onClose={() => setPlansOpen(false)} />
      </>
    );
  }

  // Suspended — 1-week grace period
  if (status === 'suspended') {
    const expiresAt = currentSchool.whitelabelExpiresAt;
    const suspendedAt = currentSchool.whitelabelSuspendedAt || expiresAt || 0;
    const graceDaysLeft = Math.max(0, Math.ceil((suspendedAt + 7 * 24 * 60 * 60 * 1000 - Date.now()) / (24 * 60 * 60 * 1000)));

    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg,#1a0b00,#0f172a)' }}>
          <div className="max-w-md text-center">
            <div className="text-6xl mb-5">⚠️</div>
            <h2 className="text-2xl font-black text-white mb-3">Subscription Suspended</h2>
            <p className="text-slate-400 mb-4">
              The subscription for <strong className="text-white">{currentSchool.name}</strong> has expired. Renew within <strong className="text-amber-400">{graceDaysLeft} day{graceDaysLeft !== 1 ? 's' : ''}</strong> to restore full access.
            </p>
            <div className="p-4 rounded-2xl mb-6 text-left" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
              <p className="text-amber-400 font-bold text-sm mb-1">Grace period active</p>
              <p className="text-slate-400 text-sm">Your account will be permanently closed in {graceDaysLeft} day{graceDaysLeft !== 1 ? 's' : ''} if not renewed. All your data is safely preserved.</p>
            </div>
            <button
              onClick={() => setPlansOpen(true)}
              className="w-full py-4 rounded-2xl font-black text-white text-base transition-all hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg,#d97706,#b45309)', boxShadow: '0 8px 24px rgba(217,119,6,0.4)' }}
            >
              Renew Subscription Now →
            </button>
            {signOut}
          </div>
        </div>
        <SubscriptionPlansModal open={plansOpen} onClose={() => setPlansOpen(false)} />
      </>
    );
  }

  // Expired — grace period passed
  return (
    <>
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg,#1a0000,#0f172a)' }}>
        <div className="max-w-md text-center">
          <div className="text-6xl mb-5">🔒</div>
          <h2 className="text-2xl font-black text-white mb-3">Account Expired</h2>
          <p className="text-slate-400 mb-4">
            The subscription for <strong className="text-white">{currentSchool.name}</strong> has expired and the grace period has ended. All your data is safely preserved — renew to restore immediate access.
          </p>
          <div className="p-4 rounded-2xl mb-6 text-left" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
            <p className="text-red-400 font-bold text-sm mb-1">Restoration is instant</p>
            <p className="text-slate-400 text-sm">As soon as your payment is confirmed your account is automatically restored. No setup required.</p>
          </div>
          <button
            onClick={() => setPlansOpen(true)}
            className="w-full py-4 rounded-2xl font-black text-white text-base transition-all hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg,#dc2626,#991b1b)', boxShadow: '0 8px 24px rgba(220,38,38,0.4)' }}
          >
            Restore Access Now →
          </button>
          <p className="text-slate-500 text-xs mt-4">
            Need help? <a href="mailto:info@muyikaratu.com" className="text-blue-400 hover:underline">info@muyikaratu.com</a>
          </p>
          {signOut}
        </div>
      </div>
      <SubscriptionPlansModal open={plansOpen} onClose={() => setPlansOpen(false)} />
    </>
  );
};

function signOutButton(onSignOut?: () => void) {
  return onSignOut && (
    <button onClick={onSignOut} className="mt-5 text-sm font-bold text-slate-300 hover:text-white underline underline-offset-4">
      ← Sign out and go back to the home page
    </button>
  );
}
